import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';

export interface SsoConfig {
  id: string;
  protocol: 'oidc' | 'saml';
  identity_provider_metadata: Record<string, unknown>;
  jit_provisioning_enabled: boolean;
  role_claim_name: string | null;
  role_claim_mapping: Record<string, string> | null;
  has_secret: boolean;
  created_at: string;
  updated_at: string;
}

export type CreateSsoConfigInput =
  | {
      protocol: 'oidc';
      identity_provider_metadata: { discovery_url: string; client_id: string; scopes: string[] };
      client_secret: string;
      jit_provisioning_enabled?: boolean;
      role_claim_name?: string | null;
      role_claim_mapping?: Record<string, string> | null;
    }
  | {
      protocol: 'saml';
      identity_provider_metadata: {
        idp_entity_id: string;
        idp_metadata_url?: string;
        idp_metadata_xml?: string;
      };
      jit_provisioning_enabled?: boolean;
      role_claim_name?: string | null;
      role_claim_mapping?: Record<string, string> | null;
    };

export interface UpdateSsoConfigInput {
  identity_provider_metadata?: Record<string, unknown>;
  client_secret?: string;
  jit_provisioning_enabled?: boolean;
  role_claim_name?: string | null;
  role_claim_mapping?: Record<string, string> | null;
}

export type TestDiscoveryResult =
  | { ok: true; issuer: string; authorization_endpoint: string; token_endpoint: string; jwks_uri: string }
  | { ok: true; idp_entity_id: string; idp_sso_url: string; idp_signing_cert_thumbprint: string | null };

const SSO_CONFIG_KEY = ['sso-config'] as const;

export function useSsoConfig() {
  return useQuery({
    queryKey: SSO_CONFIG_KEY,
    queryFn: async () => {
      const res = await apiClient.get<{ data: SsoConfig | null; errors: unknown }>('/tenant/sso-config');
      return res.data.data;
    },
    retry: (count, err: any) => err?.response?.status !== 404 && count < 2,
  });
}

export function useCreateSsoConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSsoConfigInput) => {
      const res = await apiClient.post<{ data: SsoConfig; errors: unknown }>('/tenant/sso-config', input);
      if (!res.data.data) throw new Error('Failed to create SSO configuration');
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SSO_CONFIG_KEY });
      toast.success('SSO configuration created');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.[0]?.message ?? err.message ?? 'Failed to create SSO configuration';
      toast.error(msg);
    },
  });
}

export function useUpdateSsoConfig(configId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSsoConfigInput) => {
      const res = await apiClient.patch<{ data: SsoConfig; errors: unknown }>(`/tenant/sso-config/${configId}`, input);
      if (!res.data.data) throw new Error('Failed to update SSO configuration');
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SSO_CONFIG_KEY });
      toast.success('SSO configuration updated');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.[0]?.message ?? err.message ?? 'Failed to update SSO configuration';
      toast.error(msg);
    },
  });
}

export function useDeleteSsoConfig(configId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/tenant/sso-config/${configId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SSO_CONFIG_KEY });
      toast.success('SSO configuration deleted');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.[0]?.message ?? err.message ?? 'Failed to delete SSO configuration';
      toast.error(msg);
    },
  });
}

export function useTestDiscovery(configId: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ data: TestDiscoveryResult; errors: unknown }>(
        `/tenant/sso-config/${configId}/test-discovery`,
      );
      if (!res.data.data?.ok) throw new Error('Discovery test failed');
      return res.data.data;
    },
    onSuccess: (data) => {
      if ('issuer' in data) {
        toast.success(`OIDC discovery reachable — issuer: ${data.issuer}`);
      } else {
        toast.success(`SAML metadata reachable — entity: ${data.idp_entity_id}`);
      }
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.[0]?.message ?? err.message ?? 'Discovery test failed';
      toast.error(msg);
    },
  });
}

export function useSsoInit() {
  return useMutation({
    mutationFn: async (params: { tenant_slug: string; return_to?: string }) => {
      const res = await apiClient.post<{ data: { authorization_url: string }; errors: unknown }>(
        '/auth/sso/init',
        params,
      );
      if (!res.data.data?.authorization_url) throw new Error('Failed to initiate SSO login');
      return res.data.data;
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.errors?.[0]?.message ?? err.message ?? 'SSO login failed';
      toast.error(msg);
    },
  });
}
